import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModuleAlertsPage from '../../shared/components/ModuleAlertsPage';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { InventoryConfigNotice, inventaireRecordInDateRange, sliceInventaireListItems } from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';

const InventaireAlertsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.inventaireAlertsPage.${key}`, opts);
  const inventoryConfig = useInventoryConfig();
  const { dateRange } = useSaudiListDateRange();
  const inventoryAlertsDisabled =
    !inventoryConfig.alertLowStock
    && !inventoryConfig.alertOverstock
    && !inventoryConfig.alertExpiringSoon;

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const mapStockAlert = useCallback((alert) => {
    const severityToPriority = {
      critical: 'critical',
      high: 'high',
      warning: 'medium',
      info: 'low',
    };
    const typeToUi = {
      out_of_stock: 'error',
      low_stock: 'warning',
      overstock: 'info',
      reorder_point: 'warning',
      expired: 'error',
      expiring_soon: 'warning',
    };
    const typeMessageKey = {
      out_of_stock: 'message_out_of_stock',
      low_stock: 'message_low_stock',
      overstock: 'message_overstock',
      reorder_point: 'message_reorder_point',
      expired: 'message_expired',
      expiring_soon: 'message_expiring_soon',
    };

    const productLabel = alert.product_name || alert.alert_number || p('unknown_product');
    const thresholdValue = alert.threshold_value ?? alert.reorder_point ?? alert.current_stock;
    const monetaryHint = alert.estimated_value != null
      ? p('message_value_hint', { value: formatBrandMoney(alert.estimated_value) })
      : '';

    const defaultMessage = alert.notes
      || (typeMessageKey[alert.alert_type]
        ? p(typeMessageKey[alert.alert_type], {
            product: productLabel,
            qty: thresholdValue ?? '—',
            value: monetaryHint,
          })
        : `${productLabel} (${alert.alert_type})`);

    return {
      id: alert.id,
      type: typeToUi[alert.alert_type] || 'info',
      priority: severityToPriority[alert.severity] || 'medium',
      title: alert.product_name || alert.alert_number || p(`title_${alert.alert_type}`, { defaultValue: p('alert_default_title') }),
      message: defaultMessage,
      date: (alert.triggered_date || alert.created_at || '').split('T')[0],
      read: alert.status !== 'active',
    };
  }, [p]);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/inventory/stock-alerts');
      const rows = response?.data || response || [];
      setAlerts(Array.isArray(rows) ? rows.map(mapStockAlert) : []);
    } catch (error) {
      console.error('Error loading inventory alerts:', error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [mapStockAlert]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const visibleAlerts = useMemo(() => {
    const filtered = alerts.filter((a) => inventaireRecordInDateRange(dateRange, a.date));
    return sliceInventaireListItems(filtered, inventoryConfig.itemsPerPage);
  }, [alerts, dateRange, inventoryConfig.itemsPerPage]);

  return (
    <>
      <InventoryConfigNotice
        message={inventoryAlertsDisabled ? p('notice_alerts_disabled_banner', { defaultValue: 'جميع تنبيهات المخزون معطّلة — فعّلها من إعدادات المخزون ← إعادة الطلب / انتهاء الصلاحية.' }) : null}
      />
      <ModuleAlertsPage
        moduleName={p('module_name')}
        moduleIcon={Package}
        alerts={visibleAlerts}
        loading={loading}
      />
    </>
  );
};

export default InventaireAlertsPage;
